import React, { useMemo, useRef, useState } from 'react';
import { Switch, Space, Button, Row, Col, Tooltip, Tag } from 'antd';
import { StarFilled, HolderOutlined } from '@ant-design/icons';
import TableFetchData from '../../components/data-table/TableFetchData';
import EditTableSvg from '../../assets/images/EditTable';
import DeleteTableSvg from '../../assets/images/DeleteTable';
import ViewTableSvg from '../../assets/images/ViewTable';
import { useTranslation } from 'react-i18next';
import NewPostCard from '../../components/newsManagemet/NewPostCard';
import TagTextComponent from '../../components/tag/TagTextComponent';
import { formatDateTime } from '../../utils/dateFormatUtils';
import { useNavigate } from 'react-router-dom';
import { ImageNo } from '../../components/image/ImageComponent';
import PaginationFetchData from '../../components/data-table/PaginationFetchData';
import { putDataRequest } from '../../api/serviceMethods';
import { NEWS_GET_URL, NEWS_ORDER_URL } from '../../api/URLs';
import './style.css';

const NewsTabs = ({ 
  dataSource,
  viewType,
  setViewType,
  tableParams,
  setTableParams,
  getDataRequestAll,
  loading, setLoading,
  handleStatus,
  handleDelete,
  handleView,
  handleReorderPriority,
 }) => {
  const maxLength = 20;
  const navigate = useNavigate();
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragPosition, setDragPosition] = useState(null); // 'top' | 'bottom'
  const draggedRecordRef = useRef(null);

  const { i18n, t } = useTranslation();
  let condeLang = "cn";
  const checktextByLang = (dataCode) => {
    let text = dataCode && dataCode[condeLang] && dataCode[condeLang].title ? dataCode[condeLang].title : '';
    const languages = dataCode ? Object.keys(dataCode) : [];
    if (!text) {
      for (let lang of languages) {
        if (dataCode && dataCode[lang] && dataCode[lang].title) {
          text = dataCode[lang].title;
          break;
        }
      }
    }
    return text;
  };
  const checktextByLangSub = (dataCode) => {
    let text = dataCode && dataCode[condeLang] && dataCode[condeLang].subtitle ? dataCode[condeLang].subtitle : '';
    const languages = dataCode ? Object.keys(dataCode) : [];
    if (!text) {
      for (let lang of languages) {
        if (dataCode && dataCode[lang] && dataCode[lang].subtitle) {
          text = dataCode[lang].subtitle;
          break;
        }
      }
    }
    return text;
  };

  const sortedData = useMemo(() => {
    if (!dataSource || !Array.isArray(dataSource)) return [];
    return [...dataSource].sort((a, b) => {
      const aFeature = a?.isFeature  ? 1 : 0;
      const bFeature = b?.isFeature ? 1 : 0;
      if (bFeature !== aFeature) {
        return bFeature - aFeature;
      }
      if (aFeature && bFeature) {
        if (a?.sortOrder !== undefined && b?.sortOrder !== undefined) {
          return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
        }
        if (a?.weight !== undefined && b?.weight !== undefined) {
          return (Number(b.weight) || 0) - (Number(a.weight) || 0);
        }
      }
      return 0;
    });
  }, [dataSource]);

  const savePriorityOrder = async (reorderedPriority) => {
    if (handleReorderPriority) {
      handleReorderPriority(reorderedPriority);
    } else {
      try {
        setLoading?.(true);
        const items = reorderedPriority.map((item, index) => ({
          _id: item._id || item.key || item.newsId || item.id,
          sortOrder: index + 1,
        }));
        await putDataRequest(NEWS_ORDER_URL, { items });
        getDataRequestAll?.('');
      } catch (err) {
        console.error('Failed to update priority news order:', err);
      } finally {
        setLoading?.(false);
      }
    }
  };

  const handleDragStart = (e, index, record) => {
    setDraggedIndex(index);
    draggedRecordRef.current = record;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index, record) => {
    if (draggedIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!record?.isFeature) return;

    const rowRect = e.currentTarget.getBoundingClientRect();
    const midY = rowRect.top + rowRect.height / 2;
    const position = e.clientY < midY ? 'top' : 'bottom';

    if (dragOverIndex !== index || dragPosition !== position) {
      setDragOverIndex(index);
      setDragPosition(position);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragPosition(null);
    draggedRecordRef.current = null;
  };

  const handleDrop = async (e, targetIndex, targetRecord) => {
    e.preventDefault();
    const sourceRecord = draggedRecordRef.current;
    if (!sourceRecord || !targetRecord?.isFeature) {
      handleDragEnd();
      return;
    }

    const priorityItems = sortedData.filter((item) => item?.isFeature);
    const sourcePriorityIdx = priorityItems.findIndex(
      (item) => (item.newsId || item._id) === (sourceRecord.newsId || sourceRecord._id)
    );
    const targetPriorityIdx = priorityItems.findIndex(
      (item) => (item.newsId || item._id) === (targetRecord.newsId || targetRecord._id)
    );

    if (sourcePriorityIdx === -1 || targetPriorityIdx === -1) {
      handleDragEnd();
      return;
    }

    let insertIndex = targetPriorityIdx;
    if (dragPosition === 'bottom' && sourcePriorityIdx < targetPriorityIdx) {
      insertIndex = targetPriorityIdx;
    } else if (dragPosition === 'bottom' && sourcePriorityIdx > targetPriorityIdx) {
      insertIndex = targetPriorityIdx + 1;
    } else if (dragPosition === 'top' && sourcePriorityIdx < targetPriorityIdx) {
      insertIndex = targetPriorityIdx - 1;
    } else if (dragPosition === 'top' && sourcePriorityIdx > targetPriorityIdx) {
      insertIndex = targetPriorityIdx;
    }

    insertIndex = Math.max(0, Math.min(priorityItems.length - 1, insertIndex));

    if (insertIndex === sourcePriorityIdx) {
      handleDragEnd();
      return;
    }

    const reorderedPriority = [...priorityItems];
    const [movedItem] = reorderedPriority.splice(sourcePriorityIdx, 1);
    reorderedPriority.splice(insertIndex, 0, movedItem);

    handleDragEnd();
    await savePriorityOrder(reorderedPriority);
  };

  const getRowClassName = (record, index) => {
    const classes = [];
    if (draggedIndex === index) {
      classes.push('drag-row-dragging');
    } else if (dragOverIndex === index && dragPosition === 'top') {
      classes.push('drag-row-over-top');
    } else if (dragOverIndex === index && dragPosition === 'bottom') {
      classes.push('drag-row-over-bottom');
    }
    return classes.join(' ');
  };

  const tableSettings = [
    {
      title: <label className="titleCard14400">{t("Sort")}</label>,
      width: "55px",
      align: "center",
      render: (_, record, index) => {
        if (!record?.isFeature) return null;
        return (
          <Tooltip title={t("Drag to reorder priority news")}>
            <span
              className="drag-handle"
              draggable
              onDragStart={(e) => handleDragStart(e, index, record)}
            >
              <HolderOutlined style={{ fontSize: 18, color: '#888' }} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: <label className="titleCard14400">{t("Cover")}</label>,
      render: (_, record) => (
        <div style={{ position: "relative", width: "110px", height: "74px" }}>
          <img
            style={{ width: "110px", height: "74px", borderRadius: "6px", objectFit: "cover" }}
            src={record.cover || ImageNo}
            alt="cover"
          />
          {record?.isFeature && (
            <div
              style={{
                position: "absolute",
                top: "3px",
                left: "3px",
                backgroundColor: "#E50A47",
                color: "white",
                fontSize: "10px",
                fontWeight: 600,
                padding: "1px 5px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                gap: "2px",
                boxShadow: "0 2px 4px rgba(0,0,0,0.25)"
              }}
            >
              <StarFilled style={{ fontSize: "10px" }} />
              {t("Headlines")}
            </div>
          )}
        </div>
      )
    },
    {
      title: <label className="titleCard14400">{t("News Title")}</label>,
      render: (_, record) => (
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <Tooltip title={checktextByLang(record.localize)}>
            <span>{checktextByLang(record.localize).length > maxLength ? checktextByLang(record.localize).slice(0, maxLength) + "..." : checktextByLang(record.localize)}</span>
          </Tooltip>
          {record?.isFeature && (
            <Tag
              style={{
                borderRadius: "4px",
                fontSize: "11px",
                padding: "1px 6px",
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                fontWeight: 500,
                border: "none",
                backgroundColor: "#FDECF1",
                color: "#E50A47",
              }}
            >
              <StarFilled style={{ fontSize: "11px", color: "#E50A47" }} />
              {t("Headlines")}
            </Tag>
          )}
        </div>
      )
    },
    {
      title: <label className="titleCard14400">{t("News subtitle")}</label>,
      render: (_, record) => {
        return <Tooltip title={checktextByLangSub(record.localize)}>
        {checktextByLangSub(record.localize).length > maxLength ? checktextByLangSub(record.localize).slice(0, maxLength) + "..." : checktextByLangSub(record.localize)}
      </Tooltip>
      }
    },
    {
      title: <label className="titleCard14400">{t('Category')}</label>,
      width: "100px",
      render: (_, record) => (
        <>
          <TagTextComponent textData={record.category ? [record.category] : []} />
        </>
      )
    },
    {
      title: <span class="ant-table-column-title titleCard14400">{t("Published Date")}</span>,
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      sorter: true,
      render: (_, record) => <span className='text-wrap-nowrap'>{record.publishedAt ? formatDateTime(record.publishedAt) : ''}</span>,
    },
    {
      title: <label className="titleCard14400">{t('Upload account')}</label>,
      dataIndex: 'account',
      key: 'account',
      render: (_, record) => {
        return (
          <label className="text-capitalize text-wrap-nowrap">{record && record.createdBy && record.createdBy.name ? record.createdBy.name : ''}</label>
        )
      }
    },
    {
      title: <label className="titleCard14400">{t('Operator')}</label>,
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Switch onChange={() => handleStatus(record)} checked={record.active} />
          <span onClick={() => navigate(`/news/upload?id=${record.newsId}`)} style={{ cursor: "pointer" }}>
            <EditTableSvg />
          </span>
          <span onClick={() => handleDelete(record)} style={{ cursor: "pointer" }}>
            <DeleteTableSvg />
          </span>
          <span onClick={() => handleView(record)}  style={{ cursor: "pointer" }}>
            <ViewTableSvg />
          </span>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col style={{ marginLeft: 'auto' }}>
        </Col>
      </Row>

      {viewType === 'table' ? (
        <TableFetchData
          tableParams={tableParams}
          setTableParams={setTableParams}
          data={sortedData}
          columns={tableSettings}
          loading={loading}
          setLoading={setLoading}
          rowClassName={getRowClassName}
          onRow={(record, index) => ({
            onDragOver: (e) => handleDragOver(e, index, record),
            onDrop: (e) => handleDrop(e, index, record),
            onDragEnd: handleDragEnd,
          })}
        />
      ) : (
        <>
        <NewPostCard 
            tableParams={tableParams}
            setTableParams={setTableParams}
            dataSource={sortedData}
            columns={tableSettings}
            loading={loading}
            setLoading={setLoading}
            handleStatus={handleStatus}
            handleDelete={handleDelete}
            handleView={handleView}
         />
            <PaginationFetchData
              tableParams={tableParams}
              setTableParams={setTableParams}
            />
         </>
      )}
    </div>
  );
};

export default NewsTabs;
